import {
  DIAS_SEMANA,
  HORARIO_COMERCIAL,
  ROTULO_DIA,
  type HorarioAtendimento,
} from '@mymaxx/shared';
import { Botao } from '@mymaxx/ui';
import { Copy, Plus, X } from 'lucide-react';

/**
 * Edita o horário semanal do setor. Cada dia tem zero ou mais faixas; dias sem faixa
 * significam "não atende". O botão de copiar replica o dia para toda a semana útil.
 */
export function EditorHorario({
  valor,
  onChange,
}: {
  valor: HorarioAtendimento;
  onChange: (novo: HorarioAtendimento) => void;
}) {
  const alterarFaixa = (
    dia: keyof HorarioAtendimento,
    indice: number,
    posicao: 0 | 1,
    hora: string,
  ) => {
    const faixas = valor[dia].map((f, i) =>
      i === indice ? ((posicao === 0 ? [hora, f[1]] : [f[0], hora]) as [string, string]) : f,
    );
    onChange({ ...valor, [dia]: faixas });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Horário de atendimento</span>
        <button
          type="button"
          className="text-xs text-marca-600 underline"
          onClick={() => onChange(HORARIO_COMERCIAL)}
        >
          Usar horário comercial
        </button>
      </div>

      <div className="space-y-1 rounded-card border border-neutral-200 p-3 dark:border-neutral-800">
        {DIAS_SEMANA.map((dia) => (
          <div key={dia} className="flex flex-wrap items-center gap-2 py-1">
            <span className="w-20 shrink-0 text-sm text-neutral-600 dark:text-neutral-400">
              {ROTULO_DIA[dia]}
            </span>

            {valor[dia].length === 0 && (
              <span className="text-sm text-neutral-400">não atende</span>
            )}

            {valor[dia].map((faixa, i) => (
              <span key={i} className="flex items-center gap-1">
                <input
                  type="time"
                  value={faixa[0]}
                  aria-label={`${ROTULO_DIA[dia]}: início da faixa ${i + 1}`}
                  onChange={(e) => alterarFaixa(dia, i, 0, e.target.value)}
                  className="rounded border border-neutral-300 px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
                />
                <span className="text-neutral-400">–</span>
                <input
                  type="time"
                  value={faixa[1]}
                  aria-label={`${ROTULO_DIA[dia]}: fim da faixa ${i + 1}`}
                  onChange={(e) => alterarFaixa(dia, i, 1, e.target.value)}
                  className="rounded border border-neutral-300 px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
                />
                <button
                  type="button"
                  aria-label={`Remover faixa ${i + 1} de ${ROTULO_DIA[dia]}`}
                  onClick={() =>
                    onChange({ ...valor, [dia]: valor[dia].filter((_, j) => j !== i) })
                  }
                  className="rounded p-1 text-neutral-400 hover:text-red-600"
                >
                  <X size={14} />
                </button>
              </span>
            ))}

            <button
              type="button"
              aria-label={`Adicionar faixa em ${ROTULO_DIA[dia]}`}
              onClick={() =>
                onChange({
                  ...valor,
                  [dia]: [...valor[dia], ['08:00', '18:00'] as [string, string]],
                })
              }
              className="rounded p-1 text-neutral-500 hover:text-marca-600"
            >
              <Plus size={16} />
            </button>

            {valor[dia].length > 0 && (
              <button
                type="button"
                title="Copiar para segunda a sexta"
                aria-label={`Copiar horário de ${ROTULO_DIA[dia]} para segunda a sexta`}
                onClick={() =>
                  onChange({
                    ...valor,
                    seg: [...valor[dia]],
                    ter: [...valor[dia]],
                    qua: [...valor[dia]],
                    qui: [...valor[dia]],
                    sex: [...valor[dia]],
                  })
                }
                className="rounded p-1 text-neutral-400 hover:text-marca-600"
              >
                <Copy size={14} />
              </button>
            )}
          </div>
        ))}
      </div>

      <p className="text-xs text-neutral-500">
        Fora desses horários o cliente recebe a mensagem configurada abaixo. Horário de Brasília.
      </p>
    </div>
  );
}

/** Botão pronto para limpar a semana inteira, usado no formulário. */
export function BotaoLimparHorario({ onChange }: { onChange: (h: HorarioAtendimento) => void }) {
  return (
    <Botao
      variante="secundario"
      onClick={() => onChange({ seg: [], ter: [], qua: [], qui: [], sex: [], sab: [], dom: [] })}
    >
      Limpar semana
    </Botao>
  );
}
